import {
  AlertTriangle,
  Columns2,
  FileUp,
  History,
  Keyboard,
  Layers,
  LineChart,
  Link2,
  MoreVertical,
  Pencil,
  RefreshCw,
  ShoppingCart,
  Star,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * ⋮ zákazky v hlavičke (KOLO 240) — čo patrí celej zákazke: pripojiť
 * a spojiť výrobu, import TopSolid CSV, porovnanie výpočtov, objednávky,
 * história, nálezy z importu a skratky. Akcie jednej verzie sú v ⋮ pri
 * jej kapsule (`AkcieVerzieMenu`); do KOLA 239 bolo všetko v jednom ⋮
 * pri záložkách („Spravovať výpočty").
 */
export function AkcieZakazkyMenu({
  zakazkaId,
  pocetVypoctov,
  maVyrobnu,
  onPorovnat,
  onCenaHistoria,
  pripojit = [],
  onImportTopSolid,
  onOpravy,
  onSkratky,
}: {
  zakazkaId: string;
  /** Počet výpočtov — pri jednom nie je čo porovnávať. */
  pocetVypoctov: number;
  /** Zákazka už má výrobu — import pridá ďalšiu časť. */
  maVyrobnu: boolean;
  onPorovnat: () => void;
  onCenaHistoria: () => void;
  /** Pripojiť výrobu k výpočtu (KOLO 208), spojiť s inou výrobou (KOLO 209). */
  pripojit?: { label: string; title: string; onSelect: () => void }[];
  onImportTopSolid?: () => void;
  /** Nálezy z importu TopSolidu. */
  onOpravy?: () => void;
  onSkratky: () => void;
}): JSX.Element {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 shrink-0"
          aria-label="Akcie zákazky"
          title="Pripojiť výrobu, import, porovnanie, objednávky, história, skratky"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Zákazka</DropdownMenuLabel>
        {pripojit.map((p) => (
          <DropdownMenuItem key={p.label} onSelect={p.onSelect} title={p.title}>
            <Link2 className="h-4 w-4" />
            {p.label}
          </DropdownMenuItem>
        ))}
        {onImportTopSolid ? (
          <DropdownMenuItem
            onSelect={onImportTopSolid}
            title={
              maVyrobnu
                ? "Import TopSolid CSV ako ďalšia časť výroby"
                : "Import TopSolid CSV ako výrobná varianta"
            }
          >
            <FileUp className="h-4 w-4" />
            Import TopSolid CSV
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          disabled={pocetVypoctov < 2}
          onSelect={onPorovnat}
          title={
            pocetVypoctov < 2
              ? "Na porovnanie treba aspoň dva výpočty"
              : "Rozdielová tabuľka dvoch výpočtov — súčty, zostavy, dielce"
          }
        >
          <Columns2 className="h-4 w-4" />
          Porovnať výpočty
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to={`/objednavky?zakazka_id=${zakazkaId}`}
            title="Čo treba na túto zákazku kúpiť a kto to už objednal"
          >
            <ShoppingCart className="h-4 w-4" />
            Objednávky zákazky
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            to={`/historia?zakazka_id=${zakazkaId}`}
            title="Kto, kedy a čo na tejto zákazke zmenil + odpracovaný čas"
          >
            <History className="h-4 w-4" />
            História zmien zákazky
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={onCenaHistoria}
          title="Za koľko bola zákazka ocenená pri každom prechode stavu"
        >
          <LineChart className="h-4 w-4" />
          História cien zákazky
        </DropdownMenuItem>
        {onOpravy ? (
          <DropdownMenuItem
            onSelect={onOpravy}
            title="Čo appka pri importe musela obísť — oprav to v modeli"
          >
            <AlertTriangle className="h-4 w-4" />
            Na opravu v TopSolide
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          onSelect={onSkratky}
          title="Prehľad klávesových skratiek editora (kláves ?)"
        >
          <Keyboard className="h-4 w-4" />
          Klávesové skratky
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export type DruhVerzie = "vypocet" | "vyroba" | "cast" | "tovar";

const DRUH: Record<DruhVerzie, string> = {
  vypocet: "výpočet",
  vyroba: "výrobnú variantu",
  cast: "časť",
  tovar: "rozpočet tovaru",
};

/**
 * ⋮ pri vybranej kapsule verzie (KOLO 240) — Premenovať, **Dať do ceny
 * zákazky** (výpočet = aktívny výpočet, tovar = aktívny tovar; výroba je
 * v cene sama, keď zákazka výpočet nemá), Aktualizovať z opraveného
 * exportu, Vytvoriť materiály zákazky, Zmazať.
 */
export function AkcieVerzieMenu({
  nazov,
  druh,
  onPremenovat,
  onDatDoCeny,
  vCene,
  onUpdateZExportu,
  maPovodDielcov = false,
  onSlotyZMaterialov,
  slotyBezia = false,
  onZmazat,
  mazanieBezi = false,
}: {
  nazov: string;
  druh: DruhVerzie;
  onPremenovat?: () => void;
  /** Dať do ceny zákazky — bez práva alebo pri výrobe chýba. */
  onDatDoCeny?: () => void;
  vCene: boolean;
  onUpdateZExportu?: () => void;
  /** Bez uloženého pôvodu z TopSolidu sa riadky exportu nedajú spárovať. */
  maPovodDielcov?: boolean;
  onSlotyZMaterialov?: () => void;
  slotyBezia?: boolean;
  onZmazat?: () => void;
  mazanieBezi?: boolean;
}): JSX.Element {
  const vo = druh === "vypocet" ? "vo výpočte" : druh === "tovar" ? "v tovare" : "vo výrobe";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-white/80"
          aria-label={`Akcie verzie ${nazov}`}
          title="Premenovať, dať do ceny, aktualizovať z exportu, zmazať"
        >
          <MoreVertical className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel className="max-w-[260px] truncate">{nazov}</DropdownMenuLabel>
        {onPremenovat ? (
          <DropdownMenuItem onSelect={onPremenovat}>
            <Pencil className="h-4 w-4" />
            Premenovať {DRUH[druh]}
          </DropdownMenuItem>
        ) : null}
        {onDatDoCeny ? (
          <DropdownMenuItem
            disabled={vCene}
            onSelect={onDatDoCeny}
            title={
              vCene
                ? "Táto verzia je v cene zákazky (★)"
                : "Cena zákazky a predvolená ponuka pôjdu z tejto verzie"
            }
          >
            <Star className="h-4 w-4" />
            {vCene ? "V cene zákazky" : "Dať do ceny zákazky"}
          </DropdownMenuItem>
        ) : null}
        {onUpdateZExportu ? (
          <DropdownMenuItem
            disabled={!maPovodDielcov}
            onSelect={onUpdateZExportu}
            title={
              maPovodDielcov
                ? "Chybu si opravil v TopSolide a exportoval znova? Prejdeš mapovaním " +
                  `a potom uvidíš, čo sa ${vo} zmení, kým sa to stane. Ručné úpravy sa nechávajú.`
                : "Táto verzia nemá uložený pôvod dielcov z TopSolidu (ukladá sa až od " +
                  "KOLA 117), takže sa riadky exportu nedajú spárovať. Použi nový import."
            }
          >
            <RefreshCw className="h-4 w-4" />
            Aktualizovať z opraveného exportu
          </DropdownMenuItem>
        ) : null}
        {onSlotyZMaterialov ? (
          <DropdownMenuItem
            disabled={slotyBezia}
            onSelect={onSlotyZMaterialov}
            title={
              "Z materiálov priradených priamo na dielcoch spraví materiály zákazky " +
              "M1, M2… — dekor sa potom dá vymeniť pre celú zákazku naraz. Cena sa nemení."
            }
          >
            <Layers className="h-4 w-4" />
            Vytvoriť materiály zákazky (M1, M2…)
          </DropdownMenuItem>
        ) : null}
        {onZmazat ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={mazanieBezi}
              onSelect={onZmazat}
              className="text-destructive"
              title="Zmaže verziu vrátane jej zostáv a dielcov — pýta si potvrdenie"
            >
              <Trash2 className="h-4 w-4" />
              Zmazať {DRUH[druh]} {nazov}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
